import {
  PaymentGatewayConfigurations,
  PaymentGatewayConfigurationsForm,
  PaymentGatewayConfigurationsFormSchema,
  PaymentMethodTypeEnum,
} from '@monetize/types/app';
import { Maybe, SupportedCurrencyCodes } from '@monetize/types/common';
import { formatCurrency } from '@monetize/utils/core';
import groupBy from 'lodash/groupBy';
import {
  CUSTOM_SELECT_SUBTITLE_TYPE,
  OPTION_TYPE_FIELD,
} from '../constants/customSelect';
import { PaymentMethodTypeEnumEndUserDisplay } from '../constants/paymentMethods';

type PaymentGatewayConfigurationsLike =
  | PaymentGatewayConfigurations
  | PaymentGatewayConfigurationsForm;

/**
 * Map Stripe payment element type strings to our PaymentMethodTypeEnum.
 */
const STRIPE_TYPE_TO_PAYMENT_METHOD_TYPE: Record<
  string,
  PaymentMethodTypeEnum
> = {
  card: PaymentMethodTypeEnum.CREDIT_CARD,
  us_bank_account: PaymentMethodTypeEnum.DIRECT_DEBIT,
};

export function stripeTypeToPaymentMethodType(
  stripeType: string,
): PaymentMethodTypeEnum | null {
  return STRIPE_TYPE_TO_PAYMENT_METHOD_TYPE[stripeType] ?? null;
}

const CANONICAL_PAYMENT_METHOD_ORDER = [
  PaymentMethodTypeEnum.CREDIT_CARD,
  PaymentMethodTypeEnum.DIRECT_DEBIT,
  PaymentMethodTypeEnum.US_BANK_TRANSFER,
];

export function initDefaultPaymentGatewayOptionsIfNotExists(
  supportedCurrencies: SupportedCurrencyCodes[],
  configuration?: Maybe<PaymentGatewayConfigurationsLike>,
): PaymentGatewayConfigurationsForm {
  const fallbackCurrency = supportedCurrencies[0] ?? 'USD';
  const fallbackConfiguration: PaymentGatewayConfigurationsForm = {
    configurations: [
      {
        type: PaymentMethodTypeEnum.CREDIT_CARD,
        amountConfigurations: [],
      },
      {
        type: PaymentMethodTypeEnum.DIRECT_DEBIT,
        amountConfigurations: [],
      },
      {
        type: PaymentMethodTypeEnum.US_BANK_TRANSFER,
        amountConfigurations: [
          {
            currency: fallbackCurrency,
            minimum: 0,
            maximum: null,
          },
        ],
      },
    ],
  };

  if (!configuration?.configurations.length) {
    return fallbackConfiguration;
  }

  const parsed = PaymentGatewayConfigurationsFormSchema.parse(configuration);

  // Ensure all three payment method types exist
  const existingTypes = new Set(parsed.configurations.map(({ type }) => type));
  for (const type of CANONICAL_PAYMENT_METHOD_ORDER) {
    if (!existingTypes.has(type)) {
      parsed.configurations.push({ type, amountConfigurations: [] });
    }
  }

  // Sort to canonical order
  parsed.configurations.sort(
    (a, b) =>
      CANONICAL_PAYMENT_METHOD_ORDER.indexOf(a.type) -
      CANONICAL_PAYMENT_METHOD_ORDER.indexOf(b.type),
  );

  return parsed;
}

/**
 * Validate payment amount against configured min/max limits.
 * Returns an error message if the amount is out of bounds, or null if valid.
 */
export function validatePaymentAmountLimits(
  amountLimits: { minimum: number | null; maximum: number | null } | null,
  amount: number,
  currency: string,
): string | null {
  if (!amountLimits) {
    return null;
  }
  const { minimum, maximum } = amountLimits;
  if (minimum != null && amount < minimum) {
    return `Minimum payment amount with the selected payment method is ${formatCurrency(minimum, { currency })}`;
  }
  if (maximum != null && amount > maximum) {
    return `Maximum payment amount with the selected payment method is ${formatCurrency(maximum, { currency })}`;
  }
  return null;
}

export function getPaymentAmountLimits(
  paymentConfiguration: PaymentGatewayConfigurations | null | undefined,
  paymentMethodType: PaymentMethodTypeEnum,
  currency: string,
): { minimum: number | null; maximum: number | null } | null {
  if (!paymentConfiguration?.configurations) {
    return null;
  }

  const methodConfig = paymentConfiguration.configurations.find(
    ({ type }) => type === paymentMethodType,
  );
  if (!methodConfig) {
    return null;
  }

  const amountConfig = methodConfig.amountConfigurations.find(
    ({ currency: currentCurrency }) => currentCurrency === currency,
  );

  if (!amountConfig) {
    return null;
  }

  return {
    minimum: amountConfig.minimum ?? null,
    maximum: amountConfig.maximum ?? null,
  };
}

/**
 * Group payment methods by type and return a list of items for display in custom select
 * with subtitle of payment method type
 */
export const getGroupedAndSortedPaymentMethodOptions = <
  T extends
    | { paymentMethodName: string; paymentMethodType: PaymentMethodTypeEnum }
    | { name: string; paymentMethodType: PaymentMethodTypeEnum },
>(
  paymentMethods: Array<T>,
) => {
  const sortedPaymentMethods = paymentMethods.sort((a, b) => {
    const firstName = 'paymentMethodName' in a ? a.paymentMethodName : a.name;
    const secondName = 'paymentMethodName' in b ? b.paymentMethodName : b.name;
    return firstName.localeCompare(secondName);
  });
  const paymentMethodsByType = groupBy(
    sortedPaymentMethods,
    'paymentMethodType',
  );

  return Object.keys(paymentMethodsByType).flatMap((key) => {
    const paymentMethodType = key as PaymentMethodTypeEnum;
    return [
      {
        name: PaymentMethodTypeEnumEndUserDisplay[paymentMethodType],
        [OPTION_TYPE_FIELD]: CUSTOM_SELECT_SUBTITLE_TYPE,
      },
      ...paymentMethodsByType[key],
    ];
  });
};
